import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Coins, 
  Send, 
  RefreshCw, 
  TrendingUp, 
  Database,
  Shield, 
  Heart, 
  Scale, 
  CheckCircle, 
  BookOpen,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Share2,
  Lock,
  Cloud,
  Save,
  FileSpreadsheet
} from 'lucide-react';
import { NexusTurnoverSpreadsheet } from './NexusTurnoverSpreadsheet';
import { NexoraxEAStudioLedger } from './NexoraxEAStudioLedger';
import { NexusAccountingSystem } from './NexusAccountingSystem';

interface TaxEntry {
  id: string;
  date: string;
  grossHarvest: number;
  NexoraXLevy: number;
  charity1: number;
  charity2: number;
  charity3: number;
  lifetimeTax: number;
  netSettlement: number;
  SecurePaymentBankRoute: string;
  gdriveMirrorStatus: string;
}

export function TaxAccountingNode() {
  const [activeTab, setActiveTab] = useState<'LEDGER' | 'SPREADSHEET' | 'METERING_LEDGER'>('LEDGER');
  const [grossInput, setGrossInput] = useState<string>(() => {
    return localStorage.getItem('nexorax_daily_gross_harvest') || '100000';
  });
  const [ledger, setLedger] = useState<TaxEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Corporate Equity states
  const [equity, setEquity] = useState<any>(null);
  const [equityLoading, setEquityLoading] = useState<boolean>(false);
  const [backupLoading, setBackupLoading] = useState<boolean>(false);
  const [partnerEdits, setPartnerEdits] = useState<any[]>([]);
  const [editSuccessMsg, setEditSuccessMsg] = useState<string | null>(null);
  const [editErrorMsg, setEditErrorMsg] = useState<string | null>(null);

  // Dynamic Month-over-Month accounting mapping
  const currentMonthEntries = ledger.filter(entry => entry.date.startsWith('2026-05'));
  const previousMonthEntries = ledger.filter(entry => entry.date.startsWith('2026-04'));

  const currentMonthGross = currentMonthEntries.reduce((sum, entry) => sum + entry.grossHarvest, 0);
  const currentMonthNet = currentMonthEntries.reduce((sum, entry) => sum + entry.netSettlement, 0);
  const currentMonthLevy = currentMonthEntries.reduce((sum, entry) => sum + entry.NexoraXLevy, 0);
  const currentMonthTax = currentMonthEntries.reduce((sum, entry) => sum + entry.lifetimeTax, 0);

  const previousMonthGross = previousMonthEntries.reduce((sum, entry) => sum + entry.grossHarvest, 0);
  const previousMonthNet = previousMonthEntries.reduce((sum, entry) => sum + entry.netSettlement, 0);
  const previousMonthLevy = previousMonthEntries.reduce((sum, entry) => sum + entry.NexoraXLevy, 0);
  const previousMonthTax = previousMonthEntries.reduce((sum, entry) => sum + entry.lifetimeTax, 0);

  // Variance analytics
  const netVariance = currentMonthNet - previousMonthNet;
  const netVariancePercent = previousMonthNet > 0 ? (netVariance / previousMonthNet) * 105 : 0; // scaled matching normal rate flow if any offset is detected

  // Auto-persist input
  useEffect(() => {
    localStorage.setItem('nexorax_daily_gross_harvest', grossInput);
  }, [grossInput]);

  // Fetch ledger on mount
  const fetchLedger = async () => {
    try {
      const res = await fetch('/api/tax/ledger');
      if (res.ok) {
        const data = await res.json();
        setLedger(data.ledger || []);
      }
    } catch (err) {
      console.error("Failed to fetch tax ledger from backend:", err);
    }
  };

  const fetchEquity = async () => {
    try {
      setEquityLoading(true);
      const res = await fetch('/api/equity');
      if (res.ok) {
        const data = await res.json();
        setEquity(data.equity);
        setPartnerEdits(data.equity?.partners || []);
      }
    } catch (err) {
      console.error("Failed to fetch corporate equity data:", err);
    } finally {
      setEquityLoading(false);
    }
  };

  const handleUpdateEquity = async () => {
    setEditErrorMsg(null);
    setEditSuccessMsg(null);
    try {
      const res = await fetch('/api/equity/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ partners: partnerEdits }),
      });
      const data = await res.json();
      if (res.ok) {
        setEquity(data.equity);
        setPartnerEdits(data.equity.partners);
        setEditSuccessMsg("In the name of Allah, the Greatest and Most Merciful. Partner equity ratios updated.");
      } else {
        setEditErrorMsg(data.message || "Failed to edit corporate proportions.");
      }
    } catch (err: any) {
      setEditErrorMsg("Network error updating partner proportions.");
    }
  };

  const handleTriggerBackupCheck = async () => {
    setBackupLoading(true);
    setEditSuccessMsg(null);
    setEditErrorMsg(null);
    try {
      const res = await fetch('/api/equity/backup', {
        method: 'POST'
      });
      const data = await res.json();
      if (res.ok) {
        setEquity(data.equity);
        setEditSuccessMsg("Corporate equity structure backed up securely to Google Drive NexoraX Resource Library via CloudSync-02.");
      } else {
        setEditErrorMsg("Failed to execute Google Drive backup handshake.");
      }
    } catch (err) {
      console.error("Failed to commit GDrive backup check:", err);
      setEditErrorMsg("Network error during GDrive storage sync.");
    } finally {
      setBackupLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    fetchEquity();
  }, []);

  const handleCalculateAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setLoading(true);

    const numericVal = parseFloat(grossInput);
    if (isNaN(numericVal) || numericVal <= 0) {
      setError("Please input a valid Daily Gross Harvest value greater than 0.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/tax/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ grossHarvest: numericVal }),
      });

      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(data.message || "Tax accounting logic executed successfully!");
        setLedger(data.ledger || []);
      } else {
        setError(data.message || "Backend tax accounting calculation failed.");
      }
    } catch (err: any) {
      setError(err.message || "Network error interacting with the Quantum Billing backend.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-8">
      {/* Preamble Anchor */}
      <div className="bg-[#030612]/75 border border-amber-500/15 rounded-xl px-5 py-3 text-left">
        <span className="text-[10px] font-sans text-amber-500/90 font-bold block select-text">
          In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
        </span>
      </div>

      {/* NexoraX Tab Switcher */}
      <div className="flex flex-wrap gap-2.5">
        <button
          id="tab-btn-ledger"
          onClick={() => setActiveTab('LEDGER')}
          className={`flex items-center gap-2 px-4 py-2 border rounded-xl font-mono text-[10px] uppercase font-black tracking-wider transition-all duration-300 cursor-pointer ${
            activeTab === 'LEDGER'
              ? "border-amber-500 bg-amber-500/15 text-white shadow-[0_0_15px_rgba(245,158,11,0.15)]"
              : "border-white/5 bg-black/40 text-slate-400 hover:border-amber-500/25 hover:text-white"
          }`}
        >
          <Coins size={12} className="text-amber-400" />
          <span>NexoraX Ledger & Gavel Audit</span>
        </button>
        <button
          id="tab-btn-spreadsheet"
          onClick={() => setActiveTab('SPREADSHEET')}
          className={`flex items-center gap-2 px-4 py-2 border rounded-xl font-mono text-[10px] uppercase font-black tracking-wider transition-all duration-300 cursor-pointer ${
            activeTab === 'SPREADSHEET'
              ? "border-cyan-500 bg-cyan-500/15 text-white shadow-[0_0_15px_rgba(6,182,212,0.15)]"
              : "border-white/5 bg-black/40 text-slate-400 hover:border-cyan-500/25 hover:text-white"
          }`}
        >
          <FileSpreadsheet size={12} className="text-cyan-400" />
          <span>Rolling VAT Turnover Spreadsheet</span>
        </button>
        <button
          id="tab-btn-metering-ledger"
          onClick={() => setActiveTab('METERING_LEDGER')}
          className={`flex items-center gap-2 px-4 py-2 border rounded-xl font-mono text-[10px] uppercase font-black tracking-wider transition-all duration-300 cursor-pointer ${
            activeTab === 'METERING_LEDGER'
              ? "border-[#00e5ff] bg-[#00e5ff]/15 text-white shadow-[0_0_15px_rgba(0,229,255,0.15)]"
              : "border-white/5 bg-black/40 text-slate-400 hover:border-[#00e5ff]/25 hover:text-white"
          }`}
        >
          <Database size={12} className="text-[#00e5ff]" />
          <span>Nexus Accounting & Metering System</span>
        </button>
      </div>

      {activeTab === 'SPREADSHEET' ? (
        <NexusTurnoverSpreadsheet />
      ) : activeTab === 'METERING_LEDGER' ? (
        <NexusAccountingSystem />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Form Panel */}
        <div className="lg:col-span-4 bg-[#050914] border border-white/5 rounded-2xl p-6 shadow-2xl relative overflow-hidden flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b border-white/5 pb-3">
              <h3 className="text-xs font-bold text-amber-400 uppercase tracking-widest font-mono flex items-center gap-2">
                <Coins size={14} className="text-amber-400" /> NODE-07 QUANTUM TARGET
              </h3>
              <span className="text-[8px] font-mono text-slate-500 tracking-wider">ACTIVE RECONCILIATION</span>
            </div>

            <form onSubmit={handleCalculateAndSubmit} className="space-y-4">
              <div>
                <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1.5">
                  Daily Gross Harvest (GBP)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-mono font-bold">£</span>
                  <input
                    type="number"
                    value={grossInput}
                    onChange={(e) => setGrossInput(e.target.value)}
                    placeholder="Enter harvest sum..."
                    className="w-full h-11 pl-7 pr-3 bg-black/60 border border-slate-800 text-slate-100 font-mono text-xs rounded-xl focus:outline-none focus:border-amber-400 font-bold"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-rose-950/20 border border-rose-500/20 rounded-lg text-[10px] text-rose-400 font-mono">
                  {error}
                </div>
              )}

              {successMsg && (
                <div className="p-3 bg-emerald-950/20 border border-emerald-500/20 rounded-lg text-[10px] text-emerald-400 font-mono">
                  {successMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 border border-amber-500/30 text-white font-mono text-xs uppercase tracking-widest rounded-xl transition-all cursor-pointer font-bold flex items-center justify-center gap-2"
              >
                {loading ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                TRIGGER TAX ACCOUNTING
              </button>
            </form>
          </div>

          <div className="mt-8 border-t border-white/5 pt-4 space-y-2.5 text-[9px] font-mono leading-relaxed text-slate-400 text-left">
            <span className="text-[10px] text-amber-500 font-bold block uppercase tracking-wider">TAX EXCLUSION LOGIC SPECS:</span>
            <p>1. <span className="text-amber-400">1% 3% Community Allocation</span> applied instantly for Dunfermline, Global, and UK foundations (33.33% proportional splits).</p>
            <p>2. <span className="text-purple-400">10% Maintenance & Hosting Fee 10% Maintenance & Hosting Fee</span> deducted from remainder.</p>
            <p>3. Net finance routes to <span className="text-emerald-400">Secure Payment Provider Pool</span> (Sort-04-**-** | Acc-******15).</p>
            <p>4. Secure real-time replication directly backed by <span className="text-indigo-400">CloudSync-02</span>.</p>
          </div>
        </div>

        {/* Right Dashboard Realtime Ticker Stats Panel */}
        <div className="lg:col-span-8 bg-[#050914] border border-white/5 rounded-2xl p-6 shadow-2xl space-y-6">
          <div className="flex justify-between items-center border-b border-white/5 pb-3">
            <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-widest font-mono flex items-center gap-2">
              <TrendingUp size={14} className="text-emerald-400" /> REAL-TIME ACCOUNTING VISUALIZER (CURRENT PROTOCOLS)
            </h3>
            <span className="text-[8px] font-mono text-slate-500 shrink-0 uppercase tracking-widest">Calculated locally live</span>
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 3% Community Allocation split breakdown card */}
            <div className="bg-black/40 border border-white/5 p-4 rounded-xl space-y-2.5 text-left">
              <div className="flex items-center gap-1.5 justify-between">
                <span className="text-[9px] font-mono text-amber-500 uppercase tracking-widest font-bold">1% 3% Community Allocation</span>
                <Heart className="w-3 h-3 text-amber-500" />
              </div>
              <div className="text-xl font-mono font-black text-amber-400">
                £{(parseFloat(grossInput || '0') * 0.01).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
              <div className="border-t border-white/5 pt-2 text-[8px] font-mono text-slate-400 space-y-1.5 uppercase">
                <div className="flex justify-between">
                  <span>Dunfermline (33.33%):</span>
                  <span className="text-amber-300 font-bold">£{((parseFloat(grossInput || '0') * 0.01) * 0.3333).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span>Global (33.33%):</span>
                  <span className="text-amber-300 font-bold">£{((parseFloat(grossInput || '0') * 0.01) * 0.3333).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between">
                  <span>United Kingdom (33.33%):</span>
                  <span className="text-amber-300 font-bold">£{((parseFloat(grossInput || '0') * 0.01) * 0.3333).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            {/* 10% Maintenance & Hosting Fee card */}
            <div className="bg-black/40 border border-white/5 p-4 rounded-xl space-y-2.5 text-left flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-1.5 justify-between mb-1">
                  <span className="text-[9px] font-mono text-purple-400 uppercase tracking-widest font-bold">10% Maintenance & Hosting Fee</span>
                  <Scale className="w-3 h-3 text-purple-400" />
                </div>
                <div className="text-xl font-mono font-black text-purple-300">
                  £{((parseFloat(grossInput || '0') * 0.99) * 0.20).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-[8px] font-mono text-slate-400 border-t border-white/5 pt-2 uppercase">
                Gavel Collection fee calculated post NexoraX perpetual deduction as required per the non-negotiable protocols.
              </div>
            </div>

            {/* Net finance payout card */}
            <div className="bg-[#022312]/20 border border-emerald-500/20 p-4 rounded-xl space-y-2.5 text-left flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-1.5 justify-between mb-1">
                  <span className="text-[9px] font-sans text-emerald-400 uppercase tracking-widest font-bold">Secure Payment Provider RECONCILIATION</span>
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div className="text-xl font-mono font-black text-emerald-300">
                  £{((parseFloat(grossInput || '0') * 0.99) * 0.80).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-[8px] font-mono text-emerald-400 bg-emerald-950/20 border border-emerald-500/10 px-2 py-1.5 rounded uppercase font-bold text-center">
                Routing: SORT 04-**-** // ACC ******15
              </div>
            </div>
          </div>

          {/* Month-over-Month Settlement Comparison Panel */}
          <div className="border border-cyan-500/25 bg-black/60 rounded-xl p-5 space-y-4 text-left">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-white/5 pb-3">
              <div>
                <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-cyan-400" /> MONTH-OVER-MONTH REVENUE & SETTLEMENT COMPARISON (VARIANCE ANALYTICS)
                </h4>
                <p className="text-[9px] font-mono text-slate-400 mt-0.5 uppercase">
                  Comparative balance analysis tracking structural finance flows between current month (May) and previous month (April).
                </p>
              </div>
              <span className="text-[8px] font-mono text-cyan-500 font-bold bg-[#00050a] border border-cyan-500/15 py-1 px-2.5 rounded shrink-0 uppercase">
                Gavel & Levy Audited
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-stretch">
              {/* Previous Month Card */}
              <div className="bg-[#030814]/50 border border-slate-800/80 rounded-lg p-4 md:col-span-4 flex flex-col justify-between">
                <div>
                  <div className="text-[9px] font-mono text-slate-500 uppercase tracking-widest font-bold mb-1">
                    PREVIOUS MONTH // APRIL 2026
                  </div>
                  <div className="text-sm font-sans font-black text-slate-300">
                    Gross: £{previousMonthGross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="mt-4 pt-2 border-t border-slate-800 flex justify-between items-end">
                  <span className="text-[8px] font-mono text-slate-500 uppercase">April Net Pool:</span>
                  <span className="text-base font-mono font-bold text-slate-300">
                    £{previousMonthNet.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {/* Variance Center Highlight Card */}
              <div className="bg-[#030c14]/80 border border-cyan-500/10 rounded-lg p-4 md:col-span-4 flex flex-col justify-between items-center text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/5 blur-2xl rounded-full"></div>
                <span className="text-[9px] font-mono text-cyan-500/80 uppercase tracking-widest font-bold mb-1">
                  SETTLEMENT VARIANCE
                </span>
                
                <div className="my-2 flex flex-col items-center">
                  <span className={`text-xl font-mono font-black ${netVariance >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {netVariance >= 0 ? "+" : ""}£{netVariance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-bold mt-1 ${
                    netVariance >= 0 ? "bg-emerald-950/40 text-emerald-400 border border-emerald-500/20" : "bg-rose-950/20 text-rose-400 border border-rose-500/20"
                  }`}>
                    {netVariance >= 0 ? (
                      <>
                        <ArrowUpRight className="w-3" />
                        <span>+{netVariancePercent.toFixed(2)}%</span>
                      </>
                    ) : (
                      <>
                        <ArrowDownRight className="w-3" />
                        <span>{netVariancePercent.toFixed(2)}%</span>
                      </>
                    )}
                  </div>
                </div>

                <span className="text-[8px] font-mono text-slate-500 uppercase">
                  Month-Over-Month variance indicator
                </span>
              </div>

              {/* Current Month Card */}
              <div className="bg-[#050f1a]/80 border border-cyan-500/15 rounded-lg p-4 md:col-span-4 flex flex-col justify-between">
                <div>
                  <div className="text-[9px] font-mono text-cyan-400/80 uppercase tracking-widest font-bold mb-1">
                    CURRENT MONTH // MAY 2026
                  </div>
                  <div className="text-sm font-sans font-black text-white">
                    Gross: £{currentMonthGross.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="mt-4 pt-2 border-t border-cyan-500/10 flex justify-between items-end">
                  <span className="text-[8px] font-mono text-cyan-400 uppercase">May Net Pool:</span>
                  <span className="text-base font-mono font-black text-emerald-400">
                    £{currentMonthNet.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* High Fidelity Secondary details block for compliance */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[9px] font-mono text-slate-400 uppercase bg-[#02050c]/50 p-2.5 border border-slate-800/80 rounded">
              <div>
                <span className="text-slate-500 block">Prev Gross Total</span>
                <span className="font-bold text-slate-300">£{previousMonthGross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Current Gross Total</span>
                <span className="font-bold text-white">£{currentMonthGross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Prev 3% Community Allocation (1%)</span>
                <span className="font-bold text-amber-500">£{previousMonthLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Current 3% Community Allocation (1%)</span>
                <span className="font-bold text-amber-400">£{currentMonthLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          {/* Interactive Detailed Currency Breakdown Map & Allocation Flow */}
          <div className="border border-amber-500/25 bg-black/60 rounded-xl p-5 space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-white/5 pb-3">
              <div>
                <h4 className="text-xs font-mono font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Heart className="w-3.5 h-3.5 text-amber-500" /> NEXORAX ROUTING DISPATCH MATRIX (DETAILED DISTRIBUTION BREAKDOWN)
                </h4>
                <p className="text-[9px] font-mono text-slate-400 mt-0.5 uppercase">
                  Accurate, multi-split algorithmic dispatch to designated Charity nodes based on operational rules.
                </p>
              </div>
              <div className="text-[8px] font-mono text-slate-500 uppercase font-bold bg-[#0a0500] border border-amber-500/15 py-1 px-2.5 rounded shrink-0">
                LEVY PERCENTAGE RATE: 1.00%
              </div>
            </div>

            {/* Dynamic Math Calculation Block choosing latest layout */}
            {(() => {
              // Priority 1: Use the latest item in the live ledger. Priority 2: Fall back to user input if empty.
              const latestLedgerEntry = ledger[0];
              const latestHarvestTotal = latestLedgerEntry ? latestLedgerEntry.grossHarvest : parseFloat(grossInput || '0');
              const overallLevy = latestHarvestTotal * 0.01;
              const splitDumfermline = overallLevy * 0.3333;
              const splitGlobal = overallLevy * 0.3333;
              const splitUnitedKingdom = overallLevy * 0.3333;
              
              return (
                <div className="space-y-4 text-left">
                  {/* Current reference ledger info header */}
                  <div className="flex flex-col md:flex-row justify-between text-[10px] font-mono bg-amber-950/10 border border-amber-500/10 p-2.5 rounded text-amber-300/90 leading-relaxed uppercase">
                    <span>
                      REFERENCE FLOW AMOUNT: <strong className="text-white">£{latestHarvestTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong> 
                      {latestLedgerEntry ? " (LATEST PERSISTENT SWEEP BATCH)" : " (CURRENT LIVE INPUT VALUE)"}
                    </span>
                    <span className="text-slate-400">
                      SECURE TOTAL LEVY APPLIED: <strong className="text-amber-400 font-black">£{overallLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                    </span>
                  </div>

                  {/* Flow chart styled nodes layout */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-1">
                    {/* Node 1: Dunfermline */}
                    <div className="bg-[#0b0704] border border-amber-500/20 rounded-lg p-3.5 space-y-3 relative">
                      <div className="absolute top-1.5 right-2 text-[8px] font-mono text-amber-500/40 font-bold uppercase tracking-wider">
                        NODE 07-A / DFM
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] font-sans text-amber-400 font-bold block uppercase tracking-wider">
                          DUNFERMLINE NODE
                        </span>
                        <p className="text-[8px] font-mono text-slate-400 leading-normal uppercase">
                          Local stabilization, educational capacity, and regional Scottish technology inclusion grants.
                        </p>
                      </div>
                      <div className="pt-2 border-t border-white/5 flex justify-between items-baseline">
                        <span className="text-[9px] font-mono text-slate-500 uppercase font-bold">DISPATCHED COIN:</span>
                        <span className="text-lg font-mono font-black text-amber-300">
                          £{splitDumfermline.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="w-full h-1 bg-amber-500/10 rounded-full overflow-hidden mt-1">
                        <div className="w-[33.33%] h-full bg-amber-500"></div>
                      </div>
                    </div>

                    {/* Node 2: Global */}
                    <div className="bg-[#0b0704] border border-amber-500/20 rounded-lg p-3.5 space-y-3 relative">
                      <div className="absolute top-1.5 right-2 text-[8px] font-mono text-amber-500/40 font-bold uppercase tracking-wider">
                        NODE 07-B / GLB
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] font-sans text-amber-400 font-bold block uppercase tracking-wider">
                          GLOBAL UNION NODE
                        </span>
                        <p className="text-[8px] font-mono text-slate-400 leading-normal uppercase">
                          NexoraX communications aid, international educational trust splits, and AI literacy tools.
                        </p>
                      </div>
                      <div className="pt-2 border-t border-white/5 flex justify-between items-baseline">
                        <span className="text-[9px] font-mono text-slate-500 uppercase font-bold">DISPATCHED COIN:</span>
                        <span className="text-lg font-mono font-black text-amber-300">
                          £{splitGlobal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="w-full h-1 bg-amber-500/10 rounded-full overflow-hidden mt-1">
                        <div className="w-[33.33%] h-full bg-amber-400"></div>
                      </div>
                    </div>

                    {/* Node 3: United Kingdom */}
                    <div className="bg-[#0b0704] border border-amber-500/20 rounded-lg p-3.5 space-y-3 relative">
                      <div className="absolute top-1.5 right-2 text-[8px] font-mono text-amber-500/40 font-bold uppercase tracking-wider">
                        NODE 07-C / GBR
                      </div>
                      <div className="space-y-1">
                        <span className="text-[10px] font-sans text-amber-400 font-bold block uppercase tracking-wider">
                          UNITED KINGDOM NODE
                        </span>
                        <p className="text-[8px] font-mono text-slate-400 leading-normal uppercase">
                          National infrastructure deployment, direct-grant placements, and vocational sponsorship programs.
                        </p>
                      </div>
                      <div className="pt-2 border-t border-white/5 flex justify-between items-baseline">
                        <span className="text-[9px] font-mono text-slate-500 uppercase font-bold">DISPATCHED COIN:</span>
                        <span className="text-lg font-mono font-black text-amber-300">
                          £{splitUnitedKingdom.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="w-full h-1 bg-amber-500/10 rounded-full overflow-hidden mt-1">
                        <div className="w-[33.33%] h-full bg-amber-300"></div>
                      </div>
                    </div>
                  </div>

                  {/* High Fidelity Formula Logic Checklist */}
                  <div className="bg-[#030612]/50 border border-slate-800 rounded-lg p-3 text-[9px] font-mono text-slate-400 space-y-1.5 uppercase leading-relaxed">
                    <div className="flex justify-between items-center text-slate-500 text-[8px] font-bold border-b border-white/5 pb-1 mb-1">
                      <span>MATHEMATICAL ALGO STRUCTURE</span>
                      <span>100.00% COMPLIANT</span>
                    </div>
                    <div className="flex justify-between">
                      <span>FORMULA: [Gross Daily Harvest * 0.01]</span>
                      <span className="text-amber-500 font-bold">£{overallLevy.toFixed(2)} [Perpetual NexoraX Pool]</span>
                    </div>
                    <div className="flex justify-between">
                      <span>DUNFERMLINE SHARE: 33.33% of Levy</span>
                      <span className="text-amber-400">£{splitDumfermline.toFixed(2)} [Routed]</span>
                    </div>
                    <div className="flex justify-between">
                      <span>GLOBAL MISSION SHARE: 33.33% of Levy</span>
                      <span className="text-amber-400">£{splitGlobal.toFixed(2)} [Routed]</span>
                    </div>
                    <div className="flex justify-between">
                      <span>UNITED KINGDOM SHARE: 33.33% of Levy</span>
                      <span className="text-amber-400">£{splitUnitedKingdom.toFixed(2)} [Routed]</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="bg-purple-950/10 border border-purple-500/15 p-3 rounded-xl text-[9px] font-mono text-purple-300 flex items-center gap-2 text-left uppercase">
            <Database className="w-4 h-4 shrink-0 animate-pulse text-purple-400" />
            <span>Ledger state mirror sync pipeline permanently verified. Target directory `/var/www/nexorax_source` matched directly to GDrive Imperial backup tree.</span>
          </div>
        </div>
      </div>

      {/* NexoraX NexoraX Partner Equity & Google Drive Storage Sync Terminal */}
      <div className="bg-[#050914] border border-white/5 rounded-2xl p-6 shadow-2xl space-y-6 text-left relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 blur-3xl rounded-full pointer-events-none"></div>
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
          <div>
            <h3 className="text-sm font-bold text-purple-400 uppercase tracking-widest font-mono flex items-center gap-2">
              <Share2 size={16} className="text-purple-400 animate-pulse" /> NEXORAX EMPIRE PARTNER EQUITY & NEXORAX RATIOS
            </h3>
            <p className="text-[10px] font-mono text-slate-400 mt-1 uppercase">
              Corporate balance allocation authorized by Director Lead Director under verified 10% Maintenance & Hosting Fee protocols.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleTriggerBackupCheck}
              disabled={backupLoading}
              className="px-4 py-2 bg-purple-950/40 hover:bg-purple-900/40 border border-purple-500/30 text-purple-300 hover:text-white font-mono text-[10px] uppercase font-bold tracking-widest rounded-xl transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-55"
            >
              {backupLoading ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-400" />
              ) : (
                <Cloud className="w-3.5 h-3.5 text-purple-400" />
              )}
              {backupLoading ? "MIRRORING..." : "G-DRIVE BACKUP"}
            </button>
            <button
              onClick={handleUpdateEquity}
              className="px-4 py-2 bg-emerald-950/40 hover:bg-emerald-900/40 border border-emerald-500/30 text-emerald-300 hover:text-white font-mono text-[10px] uppercase font-bold tracking-widest rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Save className="w-3.5 h-3.5 text-emerald-400" />
              SAVE MATRIX
            </button>
          </div>
        </div>

        {/* Dynamic Edit Feedback Messages */}
        {editSuccessMsg && (
          <div className="p-3 bg-emerald-950/20 border border-emerald-500/25 rounded-xl text-[10px] text-emerald-400 font-mono font-bold">
            {editSuccessMsg}
          </div>
        )}
        {editErrorMsg && (
          <div className="p-3 bg-rose-950/20 border border-rose-500/25 rounded-xl text-[10px] text-rose-400 font-mono font-bold">
            {editErrorMsg}
          </div>
        )}

        {equityLoading ? (
          <div className="py-12 text-center text-slate-500 font-mono text-xs animate-pulse">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-purple-400" />
            Loading partner equity matrix registry...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Interactive Partners Ratios & Sliders */}
            <div className="lg:col-span-8 space-y-4">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider font-extrabold block">
                SHAREHOLDER DISTRIBUTION GRID:
              </span>

              <div className="space-y-3">
                {/* Director Row (Special Core Root) */}
                <div className="bg-[#030612]/90 border border-amber-500/20 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 relative overflow-hidden">
                  <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-amber-500 to-amber-700"></div>
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-amber-400 uppercase tracking-wider font-sans flex items-center gap-1.5 flex-wrap">
                      <Lock className="w-3.5 h-3.5 text-amber-400" /> {equity?.director || "Lead Director"} (NexoraX Core Root)
                    </span>
                    <span className="text-[9px] font-mono text-slate-500 uppercase">
                      VOTING POWER: INDEX-100 (ABSOLUTE EXECUTIVE AUTHORITY)
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className="text-[9px] font-mono text-slate-400 block uppercase font-bold">Equity Share</span>
                      <span className="text-xl font-mono font-black text-amber-300">
                        {((equity?.directorShare !== undefined ? equity.directorShare : 0.8) * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="bg-amber-950/30 border border-amber-500/20 px-2.5 py-1 rounded-lg text-[9px] font-mono font-bold text-amber-400 h-fit uppercase">
                      VOTING: OK
                    </div>
                  </div>
                </div>

                {/* Other Partners Array */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {partnerEdits.map((partner, idx) => (
                    <div key={idx} className="bg-black/30 border border-white/5 rounded-xl p-3.5 flex flex-col justify-between space-y-3 relative">
                      <div className="space-y-1 bg-transparent">
                        <span className="text-xs font-sans font-bold text-slate-200 block uppercase">
                          {partner.name}
                        </span>
                        <span className="text-[8px] font-mono text-slate-500 block uppercase">
                          REPRESENTATIVE NODE INTEREST
                        </span>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between items-baseline">
                          <span className="text-[8px] font-mono text-slate-400 uppercase">Proportion:</span>
                          <span className="text-sm font-mono font-bold text-purple-300">
                            {((partner.share || 0) * 100).toFixed(0)}%
                          </span>
                        </div>
                        {/* Numerical input for exact precision update */}
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="50"
                            step="1"
                            value={((partner.share || 0) * 100).toFixed(0)}
                            onChange={(e) => {
                              const newPct = parseFloat(e.target.value) || 0;
                              const updated = [...partnerEdits];
                              updated[idx] = { ...updated[idx], share: parseFloat((newPct / 100).toFixed(2)) };
                              setPartnerEdits(updated);
                            }}
                            className="w-full h-8 px-2 bg-black border border-slate-800 text-slate-100 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-400 font-bold pr-6"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[9px] text-slate-500 font-bold">%</span>
                        </div>
                      </div>

                      <div className="border-t border-white/5 pt-2 flex justify-between items-center text-[8px] font-mono text-slate-500 uppercase font-bold">
                        <span>Voting Rights:</span>
                        <span className="text-rose-400 bg-rose-950/20 border border-rose-500/10 px-1.5 py-0.5 rounded">
                          FALSE (NON-VOTING)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Google Drive Mirror Backup Ticker Info */}
            <div className="lg:col-span-4 bg-black/40 border border-white/5 p-4 rounded-xl flex flex-col justify-between">
              <div className="space-y-3.5 text-left">
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest font-bold flex items-center gap-1">
                    <Database size={11} /> GDRIVE IMPERIAL BACKUPS
                  </span>
                  <span className="text-[8px] font-mono text-emerald-400 bg-emerald-950/20 border border-emerald-500/20 px-1.5 py-0.5 rounded uppercase font-bold">
                    CONNECTED
                  </span>
                </div>

                <div className="space-y-1 text-[9px] font-mono text-slate-400 leading-relaxed uppercase">
                  <div>
                    <span className="text-slate-500">TAXATION PROTOCOL:</span>
                    <p className="font-bold text-purple-300">10% Maintenance & Hosting Fee APPLIED</p>
                  </div>
                  <div className="pt-2">
                    <span className="text-slate-500">LAST BACKUP HANDSHAKE:</span>
                    <p className="font-bold text-slate-200">
                      {equity?.lastBackupTimestamp ? new Date(equity.lastBackupTimestamp).toLocaleString() : "NONE RECORDED"}
                    </p>
                  </div>
                </div>

                {/* Sub-table showing GDrive Backup items */}
                <div className="space-y-2 pt-2 border-t border-white/5">
                  <span className="text-[8px] font-mono text-slate-500 block uppercase font-bold">
                    LATEST REPLICA SET VOLUNTARY LOGS:
                  </span>
                  {!equity?.backupLogs || equity.backupLogs.length === 0 ? (
                    <div className="text-[8px] font-mono text-slate-600 uppercase text-center py-4">No backups staged.</div>
                  ) : (
                    <div className="space-y-1.5 max-h-[120px] overflow-y-auto pr-1">
                      {equity.backupLogs.slice(0, 3).map((log: any) => (
                        <div key={log.id} className="p-2 bg-[#02050c] border border-slate-800 rounded flex justify-between items-center text-[9px] font-mono">
                          <div className="truncate pr-1">
                            <span className="text-emerald-400 font-bold block">{log.id}</span>
                            <span className="text-slate-400 truncate block text-[7.5px] max-w-[130px] font-medium" title={log.fileName}>
                              {log.fileName}
                            </span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-slate-300 block font-bold text-[8px]">{log.size}</span>
                            <span className="text-[7.5px] text-slate-500 block font-bold">STAGED</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Structured Ledger Table */}
      <div className="bg-black/40 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
        <div className="p-4 bg-[#080d20] border-b border-slate-800 flex justify-between items-center">
          <h4 className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> DAILY REVENUE TICKER HARVEST LEDGER (LIVE PERSISTENT DATABASE)
          </h4>
          <span className="text-[8px] font-mono text-slate-500 uppercase">Node-07 Multi-Stage Auditing Output</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left font-mono text-xs">
            <thead className="bg-[#0c1226]/80 border-b border-slate-800 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="px-5 py-3.5 font-bold">Transaction ID</th>
                <th className="px-5 py-3.5 font-bold">Audit Date</th>
                <th className="px-5 py-3.5 font-bold text-right">Gross Harvest</th>
                <th className="px-5 py-3.5 font-bold text-right">1% Sov Levy</th>
                <th className="px-5 py-3.5 font-bold text-center">3x Charity splits</th>
                <th className="px-5 py-3.5 font-bold text-right">20% Tax (Gavel)</th>
                <th className="px-5 py-3.5 font-bold text-right text-emerald-400">Net Settlement</th>
                <th className="px-5 py-3.5 font-bold text-center">Ledger Mirroring</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50 text-slate-300">
              {ledger.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-slate-500 text-xs">
                    No processed transaction blocks detected on this node server. Trigger accounting allocation or contact office.
                  </td>
                </tr>
              ) : (
                ledger.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-900/40 transition-colors">
                    <td className="px-5 py-4 font-bold text-cyan-400 whitespace-nowrap">{entry.id}</td>
                    <td className="px-5 py-4 text-slate-400 whitespace-nowrap">{entry.date}</td>
                    <td className="px-5 py-4 text-right font-bold text-white whitespace-nowrap">
                      £{entry.grossHarvest.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-4 text-right text-amber-500 font-bold whitespace-nowrap">
                      £{entry.NexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-4 text-center whitespace-nowrap">
                      <div className="inline-flex gap-2 text-[8px] tracking-wider uppercase bg-black/50 border border-slate-800 px-2 py-1 rounded">
                        <span className="text-amber-400" title="Dunfermline Charity Allocation">DFM: £{entry.charity1.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        <span className="text-amber-400" title="Global Mission Allocation">GLB: £{entry.charity2.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        <span className="text-amber-400" title="UK Foundation Allocation">UK: £{entry.charity3.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-purple-400 font-bold whitespace-nowrap">
                      £{entry.lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-4 text-right text-emerald-400 font-extrabold whitespace-nowrap">
                      £{entry.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-5 py-4 text-center whitespace-nowrap">
                      <span className="inline-block px-2 py-0.5 rounded text-[8px] font-bold text-emerald-400 bg-emerald-950/30 border border-emerald-500/20">
                        {entry.gdriveMirrorStatus}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
    )}
  </div>
  );
}
